import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Grid, Icon } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { compact, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getBgColor, getElementName, getIcon } from 'pgsse/util/PgsseUtil'
import { NewTable } from 'components/datatable/NewTable'
import DangerDialog from './DangerDialog'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { getI18nTitleDataLength, getSandreLabel } from 'utils/StringUtil'
import { REDUCER_DANGERS } from 'pgsse/constant/PgsseConstants'
import { useParams } from 'react-router'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { INSTALLATION_TYPES } from 'installation/constants/InstallationConstants'
import { exportFile } from 'utils/ExportDataUtil'
import { getTruncate } from 'utils/StoreUtils'

const headers = [{ key: 'nullValue', value: ' ' }, 'family', 'type', 'group', 'precision', 'frequency', 'severity', 'cotation', 'residualRisk', 'measureTaken']
const exportHeaders = ['family', 'step', 'dangerType', 'categories', 'type', 'name', 'dangerGroup', 'precision',
    'frequency', 'severity', 'groupRisk', 'implementation', 'measureTaken',
    'measureFollowUp', 'monitoringFrequency', 'supervisor', 'correctiveAction', 'detectability' ]

const AccordionDangerTable = ({
    dangers = [],
    dispatchStateAUD = () => {},
    setIsOpen = () => {},
    setSelectedDanger = () => {},
    readMode = true,
}) => {
    const {
        sandreCodes,
        installations,
        contributors,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        installations: store.InstallationReducer.installationsIndex,
        contributors: store.ContributorReducer.contributorsIndex,
    }), shallowEqual)

    const handleDeleteElement = ({ index }) => {
        dispatchStateAUD({ type: REDUCER_DANGERS.DELETE, payload: dangers.find(d => d.index === index) })
    }

    const handleDuplicateElement = ({ index }) => {
        dispatchStateAUD({ type: REDUCER_DANGERS.ADD, payload: [{ ...dangers.find(d => d.index === index), idDanger: undefined }] })
    }

    const handleEditElement = ({ index }) => {
        setIsOpen(true)
        setSelectedDanger(dangers.find(d => d.index === index))
    }

    const formattedDangers = dangers.map(danger => {
        const cotation = (danger.frequency * danger.severity) || undefined
        const residualRisk = (cotation / danger.detectability) || undefined
        return {
            ...danger,
            family: getTruncate(getSandreLabel(sandreCodes, `${SANDRE.PGSSE_FAMILLE}.${danger.idStructure}`, danger.familyType), 30),
            type: getTruncate(getSandreLabel(sandreCodes, SANDRE.PGSSE_TYPE_DANGER, danger.dangerType), 30),
            group: getTruncate(getSandreLabel(sandreCodes, SANDRE.PGSSE_GROUP_DANGER, danger.dangerGroup), 30),
            frequency: getTruncate(getSandreLabel(sandreCodes, SANDRE.PGSSE_FREQUENCE, danger.frequency), 30),
            severity: getTruncate(getSandreLabel(sandreCodes, SANDRE.PGSSE_GRAVITE, danger.severity), 30),
            precision: getTruncate(danger.precision, 30),
            residualRisk: {
                value: residualRisk && <Box sx={{ bgcolor: getBgColor(residualRisk), color: 'white', paddingLeft: '0.5rem' }}> {residualRisk} </Box>,
                sortValue: residualRisk,
            },
            cotation,
            measureTaken: { value: danger.onSiteMeasure && <Icon>check_value</Icon>, sortValue: danger.onSiteMeasure },
            nullValue: { value: getIcon(danger, contributors, installations) },
        }
    })

    return (
        <>
            <NewTable
                rows={formattedDangers}
                headers={headers}
                lineActions={[
                    {
                        icon: 'close',
                        onClick: handleDeleteElement,
                        displayed: !readMode,
                    },
                    {
                        icon: 'queue',
                        onClick: handleDuplicateElement,
                        displayed: !readMode,
                    },
                ]}
                onClickRow={handleEditElement}
                WrapperComponent={AccordionDetails}
            />
        </>
    )
}

AccordionDangerTable.propTypes = {
    dangers: PropTypes.shape([]),
    dispatchStateAUD: PropTypes.func,
    setIsOpen: PropTypes.func,
    setSelectedDanger: PropTypes.func,
    readMode: PropTypes.bool,
}

const DangerTable = ({
    readMode = false,
    stateAUD = {},
    dispatchStateAUD,
}) => {
    const {
        dangerElements,
        sandreCodes,
        installationsIndex,
        contributorsIndex,
    } = useSelector(store => ({
        dangerElements: store.PgsseReducer.dangers,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        installationsIndex: store.InstallationReducer.installationsIndex,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
    }), shallowEqual)

    const { idDiagnostic } = useParams()
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [selectedDanger, setSelectedDanger] = useState(false)

    const structures = useSandreList(SANDRE.PGSSE_STRUCTURE)

    const stateDangerList = useMemo(() => {
        const dangersWithoutDeleted = dangerElements.filter(d => !stateAUD.deletedDangers.includes(d.idDanger))
            .map(d => stateAUD.updatedDangers.find(ud => ud.idDanger === d.idDanger) || d)
        return [
            ...dangersWithoutDeleted,
            ...stateAUD.addedDangers,
        ].map((d, index) => ({ ...d, index }))
    }, [dangerElements, stateAUD])

    const getFormattedExport = () =>
        dangerElements.map((danger, i) => {
            const cotation = (danger.frequency * danger.severity) || undefined
            const residualRisk = (cotation / danger.detectability) || undefined
            const element = danger.siteType === STATION_TYPE_CONSTANT.INSTALLATION ? installationsIndex[danger.siteCode] : contributorsIndex[danger.siteCode]
            const type = danger.siteType === STATION_TYPE_CONSTANT.INSTALLATION ? i18n[INSTALLATION_TYPES.find(it => it.code === element?.installationType)?.label] : i18n.contributor
            return {
                ...danger,
                family: getSandreLabel(sandreCodes, `${SANDRE.PGSSE_FAMILLE}.${danger.idStructure}`, danger.familyType),
                dangerType: getSandreLabel(sandreCodes, SANDRE.PGSSE_TYPE_DANGER, danger.dangerType),
                dangerGroup: getSandreLabel(sandreCodes, SANDRE.PGSSE_GROUP_DANGER, danger.dangerGroup),
                frequency: getSandreLabel(sandreCodes, SANDRE.PGSSE_FREQUENCE, danger.frequency),
                severity: getSandreLabel(sandreCodes, SANDRE.PGSSE_GRAVITE, danger.severity),
                step: getSandreLabel(sandreCodes, `${SANDRE.PGSSE_ETAPE}.${danger.idStructure}.${danger.familyType}`, danger.step),
                gravity: getSandreLabel(sandreCodes, SANDRE.PGSSE_GRAVITE, danger.gravity),
                detectability: getSandreLabel(sandreCodes, SANDRE.PGSSE_DETECTABILITE, danger.detectability),
                categories: danger.categories?.split(',').map(d => getSandreLabel(sandreCodes, SANDRE.PGSSE_CATEGORIE, d)).join(', '),
                residualRisk,
                cotation,
                measureTaken: danger.onSiteMeasure ? i18n.yes : i18n.no,
                measureFollowUp: danger.followMeans,
                supervisor: danger.monitoringContributor,
                correctiveAction: danger.correctionAction,
                type,
                name: getElementName(danger.siteType === STATION_TYPE_CONSTANT.INSTALLATION ? installationsIndex : contributorsIndex, danger.siteCode),
                headers: i === 0 ? exportHeaders : [],
            }
        })

    const actions = compact([
        !isEmpty(installationsIndex) && {
            icon: 'file_download',
            tooltip: i18n.export,
            color: 'black',
            onClick: () =>
                exportFile({
                    data: getFormattedExport(),
                    exportType: 'xlsx',
                    titleFile: i18n.dangers,
                }),
        },
        !readMode && {
            icon: 'note_add',
            tooltip: i18n.addDanger,
            onClick: () => {
                setSelectedDanger({ idDiagnostic: parseInt(idDiagnostic) })
                setIsDialogOpen(true)
            },
        }])

    return (
        <>
            <Card>
                <CardTitle title={i18n.dangers} actions={actions}/>
                <CardContent>
                    <Grid
                        container
                        spacing={1}
                        direction={'column'}
                    >
                        {structures.map((s, i) => {
                            const filteredDangers = stateDangerList.filter(({ idStructure }) => idStructure === s.code)
                            return (
                                <Accordion
                                    key={s.name}
                                    defaultExpanded={i===0}
                                >
                                    <AccordionSummary>{`${s.name} (${filteredDangers.length} ${getI18nTitleDataLength(i18n.element, i18n.elements, filteredDangers.length)})`}</AccordionSummary>
                                    <AccordionDangerTable
                                        dangers={filteredDangers}
                                        dispatchStateAUD={dispatchStateAUD}
                                        setIsOpen={setIsDialogOpen}
                                        setSelectedDanger={setSelectedDanger}
                                        readMode={readMode}
                                    />
                                </Accordion>
                            )
                        })}
                    </Grid>
                </CardContent>
            </Card>
            <DangerDialog
                isOpen={isDialogOpen}
                setIsOpen={setIsDialogOpen}
                dispatchStateAUD={dispatchStateAUD}
                danger={selectedDanger}
                readMode={readMode}
            />
        </>
    )
}

DangerTable.propTypes = {
    readMode: PropTypes.bool,
    stateAUD: PropTypes.shape({}),
    dispatchStateAUD: PropTypes.func,
}

export { AccordionDangerTable }
export default DangerTable
